/**
 * Merchant · Manual Campaign Review & Publish
 *
 * MERCHANT-ONBOARDING-REVIEW-1 · Phase 1 — merchant self-review + system
 * validation before an offer goes LIVE. No admin approval in this path.
 *
 * Route: /merchant/campaigns/:id/review
 *
 * Loads the campaign + runs POST /api/merchant/campaigns/:id/validate to
 * get authoritative READY/WARNING/BLOCKING checks, then lets the merchant
 * confirm and publish via POST /api/merchant/campaigns/:id/publish.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Rocket,
  ShieldCheck, Sparkles, Store, MapPin, Calendar, Link2, FileText,
  Percent, Tag,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY_META = {
  READY: { icon: CheckCircle2, cls: "text-emerald-600" },
  WARNING: { icon: AlertTriangle, cls: "text-amber-600" },
  BLOCKING: { icon: XCircle, cls: "text-rose-600" },
};

const SECTION_META = {
  storefront: { title: "Storefront", icon: Store },
  basics: { title: "Offer basics", icon: Tag },
  discount: { title: "Discount", icon: Percent },
  province: { title: "Province coverage", icon: MapPin },
  redemption: { title: "Redemption method", icon: Sparkles },
  coupon_gate: { title: "Checkout verification", icon: ShieldCheck },
  destination: { title: "Shopper destination", icon: Link2 },
  dates: { title: "Dates", icon: Calendar },
  terms: { title: "Terms", icon: FileText },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function SeverityIcon({ severity }) {
  const meta = SEVERITY_META[severity] || SEVERITY_META.READY;
  const Icon = meta.icon;
  return <Icon className={`w-4 h-4 shrink-0 ${meta.cls}`} />;
}

function SectionCard({ section, checks, campaign }) {
  const meta = SECTION_META[section] || { title: section, icon: Tag };
  const Icon = meta.icon;
  const anyBlock = checks.some((c) => c.severity === "BLOCKING");
  const anyWarn = checks.some((c) => c.severity === "WARNING");
  const tone = anyBlock ? "ring-rose-200 bg-rose-50/40"
             : anyWarn ? "ring-amber-200 bg-amber-50/40"
             : "ring-emerald-200 bg-emerald-50/40";
  return (
    <div className={`pf-card p-4 ring-1 ${tone}`}
         data-testid={`review-section-${section}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-700" />
        <div className="font-display text-lg text-slate-900">{meta.title}</div>
      </div>
      <ul className="space-y-2">
        {checks.map((c, i) => (
          <li key={i} className="flex items-start gap-2 text-sm"
              data-testid={`review-check-${section}-${c.severity}`}>
            <SeverityIcon severity={c.severity} />
            <div className="flex-1">
              <div className="text-slate-900">{c.label}</div>
              {c.hint && (
                <div className="text-xs text-slate-600 mt-0.5">{c.hint}</div>
              )}
              {c.fix_url && c.severity === "BLOCKING" && (
                <Link to={c.fix_url}
                      className="text-xs font-semibold text-cyan-700 hover:underline mt-1 inline-block"
                      data-testid={`review-fix-${section}`}>
                  Fix →
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MerchantCampaignReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [publishedOk, setPublishedOk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v] = await Promise.all([
        api.get(`/merchant/campaigns`).then(
          (r) => (r.data || []).find((x) => x.id === id) || null
        ),
        api.post(`/merchant/campaigns/${id}/validate`).then((r) => r.data),
      ]);
      setCampaign(c);
      setValidation(v);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.message ||
        e?.response?.data?.detail || "Failed to load campaign"
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(
        `/merchant/campaigns/${id}/publish`,
        { confirmed: true }
      );
      setPublishedOk(true);
      setConfirmModal(false);
      setCampaign(data.campaign);
      setValidation({
        checks: data.checks,
        ready_count: data.ready_count,
        warning_count: data.warning_count,
        blocking_count: data.blocking_count,
        publish_allowed: data.publish_allowed,
      });
      toast.success("Offer is live");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "publish_blocked") {
        setValidation({
          checks: detail.checks || [],
          ready_count: detail.ready_count || 0,
          warning_count: detail.warning_count || 0,
          blocking_count: detail.blocking_count || 0,
          publish_allowed: false,
        });
        toast.error(detail.message || "Publish blocked");
      } else if (detail?.error === "campaign_cap_reached") {
        toast.error(detail.message || "Plan cap reached — upgrade to publish more.");
      } else {
        toast.error(detail?.message || detail || "Publish failed");
      }
      setConfirmModal(false);
    } finally {
      setPublishing(false);
    }
  };

  const bySection = useMemo(() => {
    if (!validation?.checks) return {};
    return validation.checks.reduce((acc, c) => {
      (acc[c.section] = acc[c.section] || []).push(c);
      return acc;
    }, {});
  }, [validation]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-sm text-slate-500 flex items-center gap-2"
           data-testid="review-loading">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading offer…
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto p-6" data-testid="review-not-found">
        <p className="text-sm text-slate-600">Offer not found.</p>
        <Link to="/merchant/campaigns"
              className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold mt-3 inline-block">
          Back to offers
        </Link>
      </div>
    );
  }

  const canPublish = validation?.publish_allowed && confirmed
                     && campaign.status !== "approved";
  const isLive = campaign.status === "approved";
  const orderedSections = [
    "storefront", "basics", "discount", "province", "redemption",
    "coupon_gate", "destination", "dates", "terms",
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6"
         data-testid="merchant-campaign-review-page">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/merchant/campaigns"
              className="inline-flex items-center gap-1 hover:underline"
              data-testid="review-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Offers
        </Link>
        <span className="opacity-40">/</span>
        <span>Review &amp; publish</span>
      </div>

      <header className="space-y-2">
        <div className="pf-chip pf-chip-lav w-fit">
          <ShieldCheck className="w-3 h-3" /> Merchant self-review
        </div>
        <h1 className="font-display text-3xl text-slate-900">
          {campaign.name || "Untitled offer"}
        </h1>
        <p className="text-sm text-slate-600">
          Review every section below. When all blocking items are resolved,
          confirm and publish — your offer goes live to eligible shoppers
          immediately.
        </p>
      </header>

      {publishedOk && (
        <div className="pf-card p-4 bg-emerald-50 ring-1 ring-emerald-200 flex items-start gap-3"
             data-testid="review-published-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div className="flex-1">
            <div className="font-display text-xl text-emerald-900">
              Offer is live
            </div>
            <div className="text-sm text-emerald-800">
              Shoppers in your selected provinces can now see this offer.
            </div>
          </div>
          <Link to="/merchant/campaigns"
                className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold">
            Back to campaigns
          </Link>
        </div>
      )}

      {isLive && !publishedOk && (
        <div className="pf-card p-4 bg-emerald-50 ring-1 ring-emerald-200"
             data-testid="review-live-banner">
          <div className="font-semibold text-emerald-900">This offer is already live</div>
          <div className="text-xs text-emerald-800 mt-0.5">
            Published {fmtDate(campaign.published_at || campaign.created_at)}.
          </div>
        </div>
      )}

      {/* Summary chips */}
      <div className="flex gap-2 flex-wrap">
        <span className="pf-chip pf-chip-emerald"
              data-testid="review-summary-ready">
          <CheckCircle2 className="w-3 h-3" /> {validation?.ready_count || 0} Ready
        </span>
        <span className="pf-chip pf-chip-amber"
              data-testid="review-summary-warning">
          <AlertTriangle className="w-3 h-3" /> {validation?.warning_count || 0} Warning
        </span>
        <span className="pf-chip pf-chip-rose"
              data-testid="review-summary-blocking">
          <XCircle className="w-3 h-3" /> {validation?.blocking_count || 0} Blocking
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {orderedSections
          .filter((s) => bySection[s]?.length)
          .map((s) => (
            <SectionCard key={s} section={s}
                          checks={bySection[s]}
                          campaign={campaign} />
          ))}
      </div>

      {/* Shopper preview (representative — labelled clearly) */}
      <div className="pf-card p-5" data-testid="review-shopper-preview">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
          Offer preview
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-white to-slate-50 ring-1 ring-slate-200 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            {campaign.storefront_id ? "Cyannect storefront" : "Cyannect offer"}
          </div>
          <div className="font-display text-2xl text-slate-900 mt-1">
            {campaign.name}
          </div>
          <div className="text-sm text-slate-600 mt-1">
            {campaign.description || "Add a description to help shoppers understand the offer."}
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            <span className="pf-chip pf-chip-cyan">
              {campaign.discount_type === "percent"
                ? `${campaign.discount_value}% off`
                : campaign.discount_type === "fixed"
                ? `CA$${campaign.discount_value} off`
                : campaign.discount_type === "shipping"
                ? "Free shipping" : "BOGO"}
            </span>
            {(campaign.provinces || []).map((p) => (
              <span key={p} className="pf-chip pf-chip-lav">{p}</span>
            ))}
            <span className="pf-chip pf-chip-emerald">
              {campaign.redemption_method === "AUTO_DISCOUNT"
                ? "Automatic Discount" : "Coupon Code"}
            </span>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Representative only — the shopper app renders these details with
          its own styling.
        </div>
      </div>

      {!isLive && (
        <div className="pf-card p-5 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input type="checkbox" checked={confirmed}
                   onChange={(e) => setConfirmed(e.target.checked)}
                   data-testid="review-confirm-checkbox"
                   className="mt-1" />
            <span>
              I have reviewed this offer and confirm the details are accurate.
            </span>
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/merchant/campaigns`}
                  state={{ editDraftId: campaign.id }}
                  className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                  data-testid="review-back-to-edit">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Edit
            </Link>
            <button type="button"
                    disabled={!canPublish}
                    onClick={() => setConfirmModal(true)}
                    className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="review-publish-btn">
              <Rocket className="w-4 h-4" /> Publish Offer
            </button>
            {!validation?.publish_allowed && (
              <span className="text-xs text-rose-700"
                    data-testid="review-blocked-hint">
                Resolve the {validation?.blocking_count} blocking item{validation?.blocking_count !== 1 ? "s" : ""} above to publish.
              </span>
            )}
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => !publishing && setConfirmModal(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="glass-strong rounded-3xl w-full max-w-md p-6"
               data-testid="review-confirm-modal">
            <h3 className="font-display text-xl text-slate-900">
              Publish this offer?
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              You are about to make this offer live to eligible Cyannect
              shoppers in the selected province coverage.
            </p>
            <div className="mt-6 flex gap-2 justify-end">
              <button type="button" onClick={() => !publishing && setConfirmModal(false)}
                      className="pf-btn-glass px-4 py-2 rounded-full text-sm"
                      data-testid="review-confirm-cancel">
                Back to Edit
              </button>
              <button type="button" onClick={publish} disabled={publishing}
                      className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                      data-testid="review-confirm-publish">
                {publishing ? "Publishing…"
                             : (<><Rocket className="w-4 h-4" /> Publish Offer</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
